import React, { useEffect, useRef, useState } from 'react'
import { Container } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { AppUseDispatch, AppUseSelector } from '../../redux/hooks'
import { StoryCard } from 'components/story-card'
import UserInfo from 'components/landing-page/user-info'
import OnboardingVideoSeries from 'components/landing-page/onboarding-videos/OnboardingVideoSeries'
import UpgradeLabel from 'components/landing-page/upgrade-label'
import TrialBanner from 'components/landing-page/TrialBanner'
import WeeklyOfferGate from 'components/weekly-offer/WeeklyOfferGate'
import UNESCOStandardBanner from 'components/landing-page/UNESCOStandardBanner'
import CNYBanner from 'components/landing-page/CNYBanner'
import DragonBoatBanner from 'components/landing-page/DragonBoatBanner'
import { checkStorySubmission, getStories, getStory, getLastStep } from '../../redux/actions/storyAction'
import {
  StoryDifficultyLevel,
  LEVEL_OPTIONS,
  getStoriesByLevel,
  updatePreferredStoryLevel,
  getPreferredStoryLevel,
  getPreferredLevelSyncState,
  clearPreferredLevelSyncState,
  PreferredLevelSyncState,
} from '../../redux/actions/storyLevelAction'
import { FilterIcon } from 'containers/icons/icons'
import CountDown from 'containers/countdown/CountDown'
import SkeletonLoader from 'containers/skeleton-loader'
import 'containers/skeleton-loader/skeleton-loader.css'
import OutsideClosable from 'containers/outside-closable/OutsideClosable'
import { Trans, useTranslation } from 'react-i18next'
import { todayHeading } from 'i18n/format'
import { countDownType, membershipNameType, CHINESE_LANGUAGE_ID, PlanChangeLink } from '../../utils/constant'
import { INTRODUCTION, STORY_FINISHED } from 'routes/routeConstant'
import { LOCKED_STORY_REDIRECT_STATE } from 'utils/lockedStoryRedirect'
import {
  RESET_QUIZ,
  CLEAR_QUESTION_ANSWER_LIST,
  SET_STORY,
  SET_LAST_STEP,
  SET_STORY_LOADING,
} from '../../redux/types'
import { idValidAccount, isSunday } from 'utils/utils'
import { isUserEligibleForAprilStandardPromoBanner } from '../../utils/aprilStandardPromo'
import { isUserEligibleForCNYPromo } from '../../utils/cnyPromo'
import { isUserEligibleForDragonBoatPromo } from '../../utils/dragonBoatPromo'
import { isWeeklyReadingBlocked } from '../../utils/weeklyOffer'
import { STORY_LOCK_WEEKLY_OFFER } from '../../utils/storyAccess'
import axiosInstance from '../../axios/axiosInstance'
import '../../components/common-card/card.css'
import './stories.css'
import { serverText } from 'i18n/serverText'

const collectStoryIdsForSubmissionCheck = (stories: any[] = []) => {
  return Array.from(
    new Set(
      stories
        ?.map((story: any) => story?.id || story?.prismic_id)
        .filter((storyId): storyId is string => typeof storyId === 'string' && storyId.trim().length > 0),
    ),
  )
}

/** Rotating copy while the recommendation catches up with a level change. */
const LEVEL_SWITCH_MESSAGE_KEYS = [
  'landing:home.levelSwitching.findingLesson',
  'landing:home.levelSwitching.knownWords',
  'landing:home.levelSwitching.nextWords',
  'landing:home.levelSwitching.balance',
] as const

const Stories = () => {
  const { t } = useTranslation('landing')
  const levelSwitchMessages = LEVEL_SWITCH_MESSAGE_KEYS
  // Today as "Oct 20th 2026" in English (what moment's 'MMM Do YYYY' printed), and as the
  // language's own short date elsewhere ("22. Okt. 2026", "22 oct. 2026", "2026年10月22日").
  const todayLabel = t('home.today', { date: todayHeading(new Date()) })
  const navigate = useNavigate()
  const dispatch = AppUseDispatch()

  const { storiesListLoader, welcomeMessage, welcomeMessageKey, CTAMessage, storiesByLevel, storiesByLevelLoading } = AppUseSelector(
    ({ story }) => story,
  )
  const { credentials, membership, trialEligibility } = AppUseSelector(({ user }) => user)

  const level = credentials?.customFields?.level || credentials?.level

  const [countDownCheckTimer, setCountDownTimer] = useState(true)
  const [storiesDetail, setStoriesDetail] = useState<any>(null)
  const [showMobileInfo, setShowMobileInfo] = useState(false)
  const [selectedLevel, setSelectedLevel] = useState<StoryDifficultyLevel | null>(null)
  const [levelDropdownOpen, setLevelDropdownOpen] = useState(false)
  const [hasCheckedPreferredLevel, setHasCheckedPreferredLevel] = useState(false)
  const [mainStoryLoadingId, setMainStoryLoadingId] = useState<string | null>(null)
  const [loadingStoryId, setLoadingStoryId] = useState<string | null>(null)
  const [isLevelSwitching, setIsLevelSwitching] = useState(false)
  const [levelSwitchMessageIndex, setLevelSwitchMessageIndex] = useState(0)
  const [preferredLevelSyncState, setPreferredLevelSyncState] = useState<PreferredLevelSyncState | null>(null)
  const [weeklyLevelAck, setWeeklyLevelAck] = useState<string | null>(null)
  const [lockedReadMoreStoryId, setLockedReadMoreStoryId] = useState<string | null>(null)
  const storiesRequestRef = useRef(0)
  const isFreePlan = membership?.name === membershipNameType.Free
  const isWeekly = membership?.name === membershipNameType.Weekly

  const recommendedStory = storiesDetail?.data?.find((story: any) => story.is_recommended)
  const featuredStory = storiesDetail?.data?.find((story: any) => story.is_latest || !story.is_recommended)
  const recommendedStoryRead = recommendedStory?.is_read
  // Dragon Boat Festival (Pro, 50% off monthly for 3 months) takes precedence over the other
  // promos while its window is open and the learner is in-audience (Free incl. cohort or Weekly).
  const showDragonBoatBanner = isUserEligibleForDragonBoatPromo({
    membershipName: membership?.name,
  })
  const showAprilStandardBanner =
    !showDragonBoatBanner && isUserEligibleForAprilStandardPromoBanner(membership?.name)
  const showCNYBanner =
    !showDragonBoatBanner && !showAprilStandardBanner && isUserEligibleForCNYPromo(membership?.name)
  const shouldShowFreeTrialBanner =
    !showDragonBoatBanner &&
    !showAprilStandardBanner &&
    !showCNYBanner &&
    membership?.name === membershipNameType.Free &&
    trialEligibility?.isEligible &&
    new Date() >= new Date('2026-03-09T00:00:00')
  // Weekly subscribers read a frozen weekly pin, so the live recommendation never
  // changes mid-week — they must never enter the "matching stories" sync/poll state.
  const hasPendingRecommendationSync = Boolean(
    !isWeekly &&
    selectedLevel &&
    preferredLevelSyncState?.level === selectedLevel &&
    preferredLevelSyncState?.expectedStoryId &&
    recommendedStory?.id !== preferredLevelSyncState.expectedStoryId,
  )
  const shouldShowLevelSwitchingState = isLevelSwitching || hasPendingRecommendationSync
  const activeLevelSwitchMessage = t(levelSwitchMessages[levelSwitchMessageIndex])

  useEffect(() => {
    if (membership?.name) {
      const requestId = ++storiesRequestRef.current
      setCountDownTimer(false)
      getStories({ level, plan: membership?.name }).then((res: any) => {
        if (requestId !== storiesRequestRef.current) return
        setStoriesDetail(res)
        if (res?.CTAMessage?.button === 'Get started') {
          window.location.replace(res?.CTAMessage?.link)
        } else {
          const storyIds = res?.data?.map(({ id }) => id) || []
          if (storyIds?.length > 0) {
            checkStorySubmission({ storyIds })
          }
          setCountDownTimer(true)
        }
      })
    }
  }, [membership?.name])

  useEffect(() => {
    setPreferredLevelSyncState(getPreferredLevelSyncState())
  }, [])

  // Auto-dismiss the Weekly level-change acknowledgment after a few seconds.
  useEffect(() => {
    if (!weeklyLevelAck) {
      return
    }
    const timeoutId = setTimeout(() => setWeeklyLevelAck(null), 6000)
    return () => clearTimeout(timeoutId)
  }, [weeklyLevelAck])

  // Load preferred level
  useEffect(() => {
    if (CHINESE_LANGUAGE_ID) {
      getPreferredStoryLevel(CHINESE_LANGUAGE_ID).then((level) => {
        if (level) {
          setSelectedLevel(level as StoryDifficultyLevel)
        }
        setHasCheckedPreferredLevel(true)
      })
    } else {
      setHasCheckedPreferredLevel(true)
    }
  }, [])

  useEffect(() => {
    // Weekly subscribers only ever see two stories (the frozen recommended pin +
    // featured); the level-browse "read more" list is suppressed for them
    // (readMoreStories === []). Fetching stories-by-level here would flip
    // storiesByLevelLoading and make the featured-story card flicker out to a
    // skeleton on every level change, even though its data never changes.
    if (isWeekly) {
      return
    }
    const levelForReadMore = selectedLevel || StoryDifficultyLevel.BEGINNER_1
    getStoriesByLevel(levelForReadMore, 5)
  }, [selectedLevel, isWeekly])

  useEffect(() => {
    if (!shouldShowLevelSwitchingState) {
      setLevelSwitchMessageIndex(0)
      return
    }

    const intervalId = setInterval(() => {
      setLevelSwitchMessageIndex((current) => (current + 1) % levelSwitchMessages.length)
    }, 5000)

    return () => clearInterval(intervalId)
  }, [shouldShowLevelSwitchingState])

  useEffect(() => {
    if (!preferredLevelSyncState?.level || !selectedLevel) {
      return
    }

    // Drop stale sync markers when the selected level no longer matches.
    if (preferredLevelSyncState.level !== selectedLevel) {
      clearPreferredLevelSyncState()
      setPreferredLevelSyncState(null)
    }
  }, [preferredLevelSyncState, selectedLevel])

  useEffect(() => {
    if (!hasPendingRecommendationSync || !membership?.name) {
      return
    }

    // If the backend couldn't determine an expected story (e.g. new learner with
    // no dictionary words), stop waiting — the recommendation is already saved.
    if (!preferredLevelSyncState?.expectedStoryId) {
      clearPreferredLevelSyncState()
      setPreferredLevelSyncState(null)
      return
    }

    // Stop polling if marker is too old (e.g. user returned much later).
    if (Date.now() - preferredLevelSyncState.updatedAt > 3 * 60 * 1000) {
      clearPreferredLevelSyncState()
      setPreferredLevelSyncState(null)
      return
    }

    let isCancelled = false
    const intervalId = setInterval(async () => {
      const requestId = ++storiesRequestRef.current
      try {
        const refreshedStoriesResponse = await axiosInstance.get(
          `prismic/getIntroDB?level=${level?.toLowerCase() || ''}&plan=${membership.name}`,
        )
        if (isCancelled || requestId !== storiesRequestRef.current) {
          return
        }

        const refreshedStories = refreshedStoriesResponse?.data
        setStoriesDetail(refreshedStories)
        const storyIds = refreshedStories?.data?.map(({ id }) => id) || []
        if (storyIds?.length > 0) {
          checkStorySubmission({ storyIds })
        }

        const refreshedRecommendedStory = refreshedStories?.data?.find((story: any) => story.is_recommended)
        if (refreshedRecommendedStory?.id === preferredLevelSyncState.expectedStoryId) {
          clearPreferredLevelSyncState()
          setPreferredLevelSyncState(null)
        }
      } catch (error) {
        console.error('Failed to refresh recommended story after level change:', error)
      }
    }, 3000)

    return () => {
      isCancelled = true
      clearInterval(intervalId)
    }
  }, [hasPendingRecommendationSync, membership?.name, preferredLevelSyncState, level])

  const handleLevelSelect = async (level: StoryDifficultyLevel) => {
    setSelectedLevel(level)
    if (CHINESE_LANGUAGE_ID) {
      // Weekly subscribers get a frozen weekly pin (U4/U5): a mid-week level change
      // applies at the next Sunday drop, not now. So we persist the new preferred level
      // and acknowledge it — without clearing the current stories, entering the
      // level-switching spinner, or polling for a new recommendation (which would never
      // arrive this week and would loop the getIntroDB fetch).
      if (isWeekly) {
        try {
          await updatePreferredStoryLevel(level, CHINESE_LANGUAGE_ID)
        } catch (error) {
          console.error('Failed to update preferred level:', error)
        } finally {
          clearPreferredLevelSyncState()
          setPreferredLevelSyncState(null)
          setWeeklyLevelAck(LEVEL_OPTIONS.find((opt) => opt.value === level)?.label || level)
        }
        return
      }
      const requestId = ++storiesRequestRef.current
      setIsLevelSwitching(true)
      try {
        setStoriesDetail(null)
        await updatePreferredStoryLevel(level, CHINESE_LANGUAGE_ID)
        setPreferredLevelSyncState(getPreferredLevelSyncState())
        if (membership?.name) {
          const refreshedStoriesResponse = await axiosInstance.get(
            `prismic/getIntroDB?level=${level?.toLowerCase() || ''}&plan=${membership.name}`,
          )
          if (requestId !== storiesRequestRef.current) return
          const refreshedStories = refreshedStoriesResponse?.data
          setStoriesDetail(refreshedStories)
          const storyIds = refreshedStories?.data?.map(({ id }) => id) || []
          if (storyIds?.length > 0) {
            checkStorySubmission({ storyIds })
          }
        }
      } catch (error) {
        console.error('Failed to update preferred level:', error)
        // Re-fetch stories so the UI isn't left blank after a failed level change
        if (membership?.name) {
          try {
            const fallbackResponse = await axiosInstance.get(
              `prismic/getIntroDB?level=${level?.toLowerCase() || ''}&plan=${membership.name}`,
            )
            if (requestId === storiesRequestRef.current) {
              setStoriesDetail(fallbackResponse?.data)
              const storyIds = fallbackResponse?.data?.data?.map(({ id }) => id) || []
              if (storyIds?.length > 0) {
                checkStorySubmission({ storyIds })
              }
            }
          } catch (fallbackError) {
            console.error('Failed to re-fetch stories after level change error:', fallbackError)
          }
        }
      } finally {
        if (requestId === storiesRequestRef.current) {
          setIsLevelSwitching(false)
        }
      }
    }
  }

  const handleMainStoryClick = async (dataStory: any) => {
    setLockedReadMoreStoryId(null)
    // Free learners in the Weekly cohort are gated even on Sundays: if they get past the on-page
    // paywall overlay and click a story, send them to the plan page instead of opening it.
    if (isWeeklyReadingBlocked(membership, credentials) || dataStory?.lock_reason === STORY_LOCK_WEEKLY_OFFER) {
      navigate(PlanChangeLink)
      return
    }
    // `is_locked` is the API's own verdict, and it outranks the local day check:
    // a feed fetched on Saturday still reads as locked after midnight UTC, and
    // opening it then would show a reader with no story in it.
    // A learner's own first story opens on any day. The API marks it `is_first_story`, so the
    // local Sunday check must not lock it again.
    if (dataStory?.is_locked || (!dataStory?.is_first_story && !idValidAccount(membership?.name) && !isSunday())) {
      navigate(STORY_FINISHED, { state: LOCKED_STORY_REDIRECT_STATE })
      return
    }
    setMainStoryLoadingId(dataStory?.id)
    try {
      const lastStep = await getLastStep({
        storyId: dataStory.id,
        level: dataStory?.level?.toLowerCase() || '',
      })
      let finalStoryIntroduction = dataStory.storyIntroduction?.toString() || ''
      const re = new RegExp(String.fromCharCode(160), 'g')
      finalStoryIntroduction = finalStoryIntroduction?.replace(re, ' ')
      dataStory.englishContent = dataStory.englishContent
      dataStory.storyContent = dataStory.arContentWithTags
      dataStory.storyIntroduction = finalStoryIntroduction
      dataStory.arSentencesWord11 = dataStory?.arSentencesWord11 ?? []
      dataStory.arSentencesWord12 = dataStory?.arSentencesWord12 ?? []
      dataStory.arSentencesWord21 = dataStory?.arSentencesWord21 ?? []
      dataStory.arSentencesWord22 = dataStory?.arSentencesWord22 ?? []
      dataStory.arContent = [
        ...(dataStory?.arContent ?? []),
        ...dataStory.arSentencesWord11,
        ...dataStory.arSentencesWord12,
        ...dataStory.arSentencesWord21,
        ...dataStory.arSentencesWord22,
      ]
      dispatch({ type: SET_STORY, payload: dataStory })
      dispatch({
        type: SET_LAST_STEP,
        payload: lastStep?.data?.[0]?.data?.step,
      })
      dispatch({ type: SET_STORY_LOADING, payload: { storyLoading: false } })
      dispatch({ type: RESET_QUIZ })
      dispatch({ type: CLEAR_QUESTION_ANSWER_LIST })
      navigate(INTRODUCTION)
    } finally {
      setMainStoryLoadingId(null)
    }
  }

  const selectedLevelStories =
    storiesByLevel?.filter(
      (story: any) => story?.id !== featuredStory?.id && story?.prismic_id !== featuredStory?.prismic_id,
    ) || []
  // Weekly subscribers get exactly two stories (recommended pin + featured); the extra
  // level-browse stories are not part of their plan, so the "read more" list is suppressed.
  const readMoreStories = isWeekly ? [] : selectedLevelStories.slice(0, 2)

  useEffect(() => {
    if (!membership?.name) {
      return
    }

    const combinedStories = [...(storiesDetail?.data || []), ...(storiesByLevel || [])]
    const storyIds = collectStoryIdsForSubmissionCheck(combinedStories)
    if (storyIds.length > 0) {
      checkStorySubmission({ storyIds })
    }
  }, [membership?.name, storiesDetail?.data, storiesByLevel])

  const handleLevelStoryClick = async (story: any) => {
    if (loadingStoryId) return
    setLockedReadMoreStoryId(null)
    const storyLevel = story.category?.toLowerCase() || 'beginner'
    setLoadingStoryId(story.prismic_id)
    try {
      dispatch({ type: RESET_QUIZ })
      dispatch({ type: CLEAR_QUESTION_ANSWER_LIST })
      await getStory(story.prismic_id, storyLevel)
      navigate(INTRODUCTION)
    } catch (error) {
      console.error('Failed to load story:', error)
    } finally {
      setLoadingStoryId(null)
    }
  }

  const handleReadMoreStoryClick = (story: any) => {
    if (isFreePlan) {
      setLockedReadMoreStoryId(story?.prismic_id || story?.id || null)
      return
    }
    handleLevelStoryClick(story)
  }

  const handleUpgradeClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setLockedReadMoreStoryId(null)
    navigate(PlanChangeLink)
  }

  return (
    <Container className='custom_container'>
      <WeeklyOfferGate placement='stories' />
      <UserInfo title={serverText(welcomeMessageKey ? `landing:home.welcome.${welcomeMessageKey}` : null, welcomeMessage)} />
      <OnboardingVideoSeries />
      <div className='card_sec_main_div'>
        <div className='date-row'>
          <h4 className='main_title_text'>
            {todayLabel}{' '}
            {!storiesListLoader && (
              <>
                <div className='mobile_info_button' onClick={() => setShowMobileInfo(!showMobileInfo)}>
                  <div className='info_icon'>{t('home.infoIcon')}</div>
                </div>
                {countDownCheckTimer && (
                  <span>
                    {recommendedStory && recommendedStoryRead ? (
                      isWeekly ? (
                        t('home.doneThisWeek')
                      ) : (
                        t('home.doneToday')
                      )
                    ) : (
                      <Trans
                        t={t}
                        i18nKey='home.nextLessonIn'
                        components={{
                          countdown: (
                            <CountDown type={isWeekly ? countDownType.Week : countDownType.Day} color={'none'} />
                          ),
                        }}
                      />
                    )}
                  </span>
                )}
              </>
            )}
          </h4>
          {!storiesListLoader && (
            <div className='position-relative'>
              {hasCheckedPreferredLevel && !selectedLevel && !levelDropdownOpen && (
                <div className='difficulty-tooltip-nudge' role='status' aria-live='polite'>
                  <span className='difficulty-tooltip-nudge__title'>{t('home.levelNudgeTitle')}</span>
                  <span className='difficulty-tooltip-nudge__text'>{t('home.levelNudgeText')}</span>
                </div>
              )}
              <span
                className='action_btn_text d-flex align-items-center gap-2 p-0'
                onClick={() => setLevelDropdownOpen(!levelDropdownOpen)}
              >
                <FilterIcon color={'#373737'} />
                {selectedLevel
                  ? LEVEL_OPTIONS.find((opt) => opt.value === selectedLevel)?.label || t('home.adjustDifficulty')
                  : t('home.adjustDifficulty')}
              </span>
              <OutsideClosable isOpen={levelDropdownOpen} onClose={() => setLevelDropdownOpen(false)}>
                <div className='browse-level-dropdown browse-level-dropdown--right'>
                  <p className='modal_para_text'>
                    <Trans t={t} i18nKey='home.levelPicker' components={[<span key='level' />]} />
                  </p>
                  <div className='browse-level-tabs'>
                    {LEVEL_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        className={`browse-level-tab ${selectedLevel === option.value ? 'browse-level-tab--active' : ''}`}
                        onClick={() => {
                          handleLevelSelect(option.value)
                          setLevelDropdownOpen(false)
                        }}
                      >
                        <span className='browse-level-tab-label'>{option.label}</span>
                        <span className='browse-level-tab-description'>{option.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </OutsideClosable>
            </div>
          )}
        </div>
        {isWeekly && weeklyLevelAck && (
          <div className='weekly-level-ack' role='status' aria-live='polite'>
            <Trans
              t={t}
              i18nKey='home.weeklyLevelAck'
              values={{ level: weeklyLevelAck }}
              components={{ strong: <strong /> }}
            />
          </div>
        )}
        {showDragonBoatBanner && <DragonBoatBanner />}
        {showAprilStandardBanner && <UNESCOStandardBanner />}
        {showCNYBanner && <CNYBanner />}
        {shouldShowFreeTrialBanner && <TrialBanner trialEligibility={trialEligibility} />}
        {storiesListLoader ? (
          <SkeletonLoader />
        ) : (
          shouldShowLevelSwitchingState ? (
            <div className='recommended-refresh-inline'>
              <p className='recommended-refresh-inline-text'>
                {activeLevelSwitchMessage}
                <span className='recommended-refresh-dots' aria-hidden='true'>
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </span>
              </p>
            </div>
          ) : recommendedStory ? (
            <StoryCard
              key={recommendedStory?.id}
              story={recommendedStory}
              variant="hero"
              onClick={handleMainStoryClick}
              loading={mainStoryLoadingId === recommendedStory?.id}
            />
          ) : (
            <p className='found_text'>{t('home.storyNotFound')}</p>
          )
        )}
      </div>
      {!storiesListLoader && CTAMessage?.message && !shouldShowFreeTrialBanner && <UpgradeLabel />}

      {!storiesListLoader && (
        <>
          <div className='read-more-section'>
            <h4 className='read-more-title'>{t('home.readMoreTitle')}</h4>

            {storiesByLevelLoading ? (
              <div className='browse-level-loading browse-level-loading--skeleton'>
                <div
                  className='skeleton_loader_cls'
                  style={{ width: '220px', height: '28px', borderRadius: '6px', marginBottom: '14px' }}
                />
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className='skeleton_loader_card_div'
                    style={{
                      padding: '20px 25px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      minHeight: 'auto',
                      marginBottom: i < 3 ? '12px' : 0,
                    }}
                  >
                    <div
                      className='skeleton_loader_cls'
                      style={{ width: '60px', minWidth: '60px', height: '44px', borderRadius: '6px' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        className='skeleton_loader_cls'
                        style={{ width: '70%', height: '22px', marginBottom: '8px' }}
                      />
                      <div
                        className='skeleton_loader_cls'
                        style={{ width: '90%', height: '18px' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : featuredStory || readMoreStories.length > 0 ? (
              <div className='browse-level-list'>
                {featuredStory && (
                  <StoryCard
                    story={featuredStory}
                    variant="compact"
                    displayTags={['featured']}
                    showLevelBadge={false}
                    onClick={handleMainStoryClick}
                    loading={mainStoryLoadingId === featuredStory?.id}
                  />
                )}
                {readMoreStories.map((story: any) => (
                  <div
                    key={story.id}
                    className='seemore-tooltip read-more-locked-tooltip-anchor'
                  >
                    <StoryCard
                      story={story}
                      variant="compact"
                      showLevelBadge={false}
                      onClick={handleReadMoreStoryClick}
                      loading={loadingStoryId === story.prismic_id}
                    />
                    {isFreePlan && lockedReadMoreStoryId === (story?.prismic_id || story?.id) && (
                      <div className='disable-correction-tooltip read-more-lock-tooltip'>
                        <div id='tooltiptextWriter' className='tooltiptextWriter' onClick={(event) => event.stopPropagation()}>
                          <button
                            className='tooltiptext-close'
                            onClick={(event) => {
                              event.stopPropagation()
                              setLockedReadMoreStoryId(null)
                            }}
                            aria-label={t('home.closeUpgradeTooltip')}
                          >
                            <i className='fa fa-times' aria-hidden='true'></i>
                          </button>
                          <Trans
                            t={t}
                            i18nKey='home.readMoreLocked'
                            components={[
                              <a
                                key='upgrade'
                                onClick={handleUpgradeClick}
                                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                              />,
                            ]}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className='browse-level-empty'>{t('home.readMoreEmpty')}</p>
            )}
          </div>
        </>
      )}

      {/* Mobile Info Modal */}
      {showMobileInfo && (
        <div className='mobile_info_modal' onClick={() => setShowMobileInfo(false)}>
          <div className='mobile_info_content' onClick={(e) => e.stopPropagation()}>
            <p className='mobile_info_text'>
              <Trans t={t} i18nKey='home.infoYourLesson' components={[<strong key='label' />]} />
            </p>
            <p className='mobile_info_text'>
              <Trans t={t} i18nKey='home.infoFeaturedLesson' components={[<strong key='label' />]} />
            </p>
          </div>
        </div>
      )}
    </Container>
  )
}

export default Stories
